// Opening move: Shared helpers for section boundary navigation and affordances
// Big play: Extract reusable logic for creating/reusing paragraph siblings
// This keeps boundary escape logic consistent between keyboard nav and mouse affordances

import {
  $createParagraphNode,
  $getSelection,
  $isRangeSelection,
  $isElementNode,
  $getRoot,
  type LexicalNode,
  type ParagraphNode,
} from 'lexical'
import { $isSectionBlockNode, type SectionBlockNode } from '../nodes/SectionBlockNode'

/**
 * ensureParagraphSibling - Create or reuse an untagged paragraph adjacent to a section
 *
 * Main play: Materializes a paragraph sibling either before or after a section block
 * Big play: Reuses existing paragraph siblings to avoid piling up empty nodes
 *
 * @param sectionBlock - The SectionBlockNode to insert relative to
 * @param position - 'before' or 'after' to specify insertion point
 * @returns The paragraph node (newly created or reused existing)
 */
export function ensureParagraphSibling(
  sectionBlock: SectionBlockNode,
  position: 'before' | 'after'
): ParagraphNode {
  // Time to tackle the tricky bit: Check if reusable sibling already exists
  const sibling = position === 'before'
    ? sectionBlock.getPreviousSibling()
    : sectionBlock.getNextSibling()

  // Big play: Reuse existing paragraph sibling if available
  if (sibling && sibling.getType() === 'paragraph') {
    return sibling as ParagraphNode
  }

  // Victory lap: No existing paragraph, create new one
  const newParagraph = $createParagraphNode()

  if (position === 'before') {
    sectionBlock.insertBefore(newParagraph)
  } else {
    sectionBlock.insertAfter(newParagraph)
  }

  return newParagraph
}

/**
 * isAtSectionBoundary - Check if cursor is at the boundary of a section block
 *
 * Main play: Detects when writer is at the end of the last child in a section
 * Big play: Ensures we only create escape paragraphs when truly at a boundary
 *
 * @param position - 'start' or 'end' to specify which boundary to check
 * @returns { inSection: boolean, section: SectionBlockNode | null }
 */
export function isAtSectionBoundary(
  position: 'start' | 'end'
): { inSection: boolean; section: SectionBlockNode | null } {
  const selection = $getSelection()

  // Early return: Only handle collapsed range selections
  if (!$isRangeSelection(selection) || !selection.isCollapsed()) {
    return { inSection: false, section: null }
  }

  const anchor = selection.anchor
  const anchorNode = anchor.getNode()

  // Time to tackle the tricky bit: Walk up tree to find containing SectionBlockNode
  let current: LexicalNode | null = anchorNode
  let sectionBlock: SectionBlockNode | null = null

  while (current) {
    if ($isSectionBlockNode(current)) {
      sectionBlock = current
      break
    }
    current = current.getParent()
  }

  // Early return: Not in a section block
  if (!sectionBlock) {
    return { inSection: false, section: null }
  }

  // Big play: Verify section is at root level (required for escape)
  const sectionParent = sectionBlock.getParent()
  const root = $getRoot()
  if (sectionParent !== root) {
    return { inSection: false, section: null }
  }

  // Time to tackle the tricky bit: Check boundary position
  if (position === 'end') {
    // Check if we're at the end of the last child
    const lastChild = sectionBlock.getLastChild()
    if (!lastChild) {
      return { inSection: false, section: null }
    }

    // Check if selection is in the last child
    const selectionParent = anchorNode.getType() === 'text'
      ? anchorNode.getParent()
      : anchorNode

    if (selectionParent !== lastChild) {
      return { inSection: false, section: null }
    }

    // Check if cursor is at the end of content
    const offset = anchor.offset
    const isAtEnd = anchorNode.getType() === 'text'
      ? offset === anchorNode.getTextContentSize()
      : $isElementNode(lastChild) ? offset === lastChild.getChildrenSize() : false

    if (!isAtEnd) {
      return { inSection: false, section: null }
    }

    // Victory lap: Confirmed at section boundary
    return { inSection: true, section: sectionBlock }
  } else {
    // Check if we're at the start of the first child
    const firstChild = sectionBlock.getFirstChild()
    if (!firstChild) {
      return { inSection: false, section: null }
    }

    // Check if selection is in the first child
    const selectionParent = anchorNode.getType() === 'text'
      ? anchorNode.getParent()
      : anchorNode

    if (selectionParent !== firstChild) {
      return { inSection: false, section: null }
    }

    // Check if cursor is at the start of content
    const offset = anchor.offset
    const isAtStart = offset === 0

    if (!isAtStart) {
      return { inSection: false, section: null }
    }

    // Victory lap: Confirmed at section boundary
    return { inSection: true, section: sectionBlock }
  }
}

/**
 * getSectionBlockGeometry - Get DOM bounding boxes for section blocks
 *
 * Main play: Calculate positions for hover affordances relative to editor container
 * Big play: Returns scroll-aware coordinates suitable for absolutely positioned overlays
 *
 * Reference: .local_docs/lexical/articles/overlay-positioning.md
 *
 * @param editor - Lexical editor instance
 * @returns Array of section geometries with keys and bounding boxes
 */
export function getSectionBlockGeometry(
  editor: { getRootElement: () => HTMLElement | null; getElementByKey: (key: string) => HTMLElement | null }
): Array<{
  key: string
  rect: DOMRect
  index: number
}> {
  const rootElement = editor.getRootElement()
  if (!rootElement) return []

  const root = $getRoot()
  const sections = root.getChildren().filter($isSectionBlockNode)

  // Main play: Map sections to their DOM geometries
  return sections
    .map((section, index) => {
      const key = section.getKey()
      const dom = editor.getElementByKey(key)
      if (!dom) return null

      return {
        key,
        rect: dom.getBoundingClientRect(),
        index,
      }
    })
    .filter((item): item is NonNullable<typeof item> => item !== null)
}

// Victory lap: Shared helpers enable consistent paragraph creation and boundary detection
// Keyboard navigation and mouse affordances now use identical logic for reliability
